import { useState, useEffect, useRef } from 'react';

const TimePicker = ({ value, onChange, error, required = false, disabled = false, readOnly = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Initialize hours and minutes from the value (format: HH:MM:SS or HH:MM)
  const initialTime = value ? value.split(':') : ['00', '00'];
  const [hours, setHours] = useState(parseInt(initialTime[0]) || 0);
  const [minutes, setMinutes] = useState(parseInt(initialTime[1]) || 0);

  // Update internal state when value prop changes
  useEffect(() => {
    if (value) {
      const parts = value.split(':');
      setHours(parseInt(parts[0]) || 0);
      setMinutes(parseInt(parts[1]) || 0);
    }
  }, [value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const updateTime = (newHours, newMinutes) => {
    // Normalize hours (0-24) and minutes (0-60)
    let h = Math.max(0, Math.min(24, newHours));
    let m = Math.max(0, Math.min(60, newMinutes));
    
    setHours(h);
    setMinutes(m);
    
    // Format: HH:MM:SS (keeping SS as 00 as seen in the original code's manual string)
    const formattedHours = h.toString().padStart(2, '0');
    const formattedMinutes = m.toString().padStart(2, '0');
    onChange(`${formattedHours}:${formattedMinutes}:00`);
  };

  const handleHourChange = (delta) => {
    updateTime(hours + delta, minutes);
  };

  const handleMinuteChange = (delta) => {
    let newMinutes = minutes + delta;
    let newHours = hours;

    if (newMinutes > 60) {
      newMinutes = 0;
      newHours = Math.min(24, newHours + 1);
    } else if (newMinutes < 0) {
      newMinutes = 60;
      newHours = Math.max(0, newHours - 1);
    }

    updateTime(newHours, newMinutes);
  };

  const displayHours = hours.toString().padStart(2, '0');
  const displayMinutes = minutes.toString().padStart(2, '0');
  const inputValue = `${displayHours}:${displayMinutes}`;

  const ArrowUp = () => (
    <svg className="w-8 h-4 text-gray-500 cursor-pointer hover:text-jet-yellow transition-colors" viewBox="0 0 24 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 10L12 2L22 10" />
    </svg>
  );

  const ArrowDown = () => (
    <svg className="w-8 h-4 text-gray-500 cursor-pointer hover:text-jet-yellow transition-colors" viewBox="0 0 24 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 2L12 10L22 2" />
    </svg>
  );

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <input
        type="text"
        readOnly
        value={inputValue}
        onClick={() => !disabled && !readOnly && setIsOpen(!isOpen)}
        placeholder="00:00"
        className={`w-full h-[34px] py-2 px-3 text-[13px] text-[#333] bg-white border ${error ? 'border-red-500' : 'border-[#ddd]'} rounded box-border transition-[border-color] duration-200 ${disabled || readOnly ? 'cursor-not-allowed bg-[#f5f5f5]' : 'cursor-pointer focus:outline-none focus:border-[#ffd200]'}`}
      />
      
      {isOpen && !disabled && !readOnly && (
        <div className="absolute top-full left-0 mt-3 bg-white border border-gray-100 rounded-lg shadow-[0_10px_25px_rgba(0,0,0,0.1)] z-50 p-4 min-w-[150px] flex flex-col items-center select-none">
          {/* Arrow Pointer */}
          <div className="absolute -top-1.5 left-8 w-3 h-3 bg-white border-t border-l border-gray-100 rotate-45"></div>
          
          <p className="text-[12px] text-gray-400 font-normal mb-4 mt-0 uppercase tracking-wider">Select Time</p>
          
          <div className="grid grid-cols-3 items-center gap-x-2">
            {/* Hour Up */}
            <div className="flex justify-center" onClick={() => handleHourChange(1)}>
              <ArrowUp />
            </div>
            <div></div>
            {/* Minute Up */}
            <div className="flex justify-center" onClick={() => handleMinuteChange(1)}>
              <ArrowUp />
            </div>

            {/* Time Display */}
            <div className="text-[28px] font-bold text-[#333] text-center w-10">
              {displayHours}
            </div>
            <div className="text-[24px] font-bold text-[#333] text-center px-1">
              :
            </div>
            <div className="text-[28px] font-bold text-[#333] text-center w-10">
              {displayMinutes}
            </div>

            {/* Hour Down */}
            <div className="flex justify-center" onClick={() => handleHourChange(-1)}>
              <ArrowDown />
            </div>
            <div></div>
            {/* Minute Down */}
            <div className="flex justify-center" onClick={() => handleMinuteChange(-1)}>
              <ArrowDown />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
